/**
 * The chart marks the dashboard cards are drawn from — a sparkline, an hourly bar row, a ratio
 * gauge and a stacked severity bar.
 *
 * They exist because a summary figure answers "how many" and nothing else. Six intrusions spread
 * across the day and six in the last twenty minutes are the same number and completely different
 * situations, and the only honest way to tell them apart on a card is to show the shape of the
 * day beside the count. Every mark here is fed by the SAME payload the figure above it comes
 * from (/api/analytics/summary), so a chart can never disagree with the number it sits under.
 *
 * ── Rules these follow, and why ──
 *
 * 1. NO VALUE IS TOOLTIP-ONLY. Every chart ships with its figures stated in text — the headline,
 *    the axis ends, the legend counts. The hover titles are an enhancement for reading a
 *    specific hour, never the only way to learn what the mark says.
 *
 * 2. THE LEGEND IS THE INSTRUMENT, not the colour. `--warning` (#eab308) measures 1.7:1 against
 *    the light-mode tile surface — too weak to carry a value as a thin bar segment on its own —
 *    and it is the token the severity vocabulary uses everywhere else in the product, so it
 *    can't be swapped here without the bar disagreeing with the pills beside it. Every
 *    multi-colour mark below therefore renders its counts as text. (Adjacent-hue separation is
 *    fine: destructive↔warning is ΔE 16.3 under deuteranopia, well clear of the ΔE 8 target.)
 *
 * 3. A ZERO BUCKET IS DRAWN. The SQL only returns hours that had rows; an hour with none is a
 *    real zero and gets a track-coloured stub, because a chart that silently closes its gaps
 *    turns a quiet afternoon into a continuous line.
 *
 * 4. EMPHASIS, NOT A RAMP. Where one bucket matters (the peak, the current hour) it takes the
 *    full colour and the rest step back to a tint. Shading every bar by its own value would
 *    double-encode height as hue and spend the only free channel on what the bars already show.
 */
import type { ReactNode } from 'react';

/**
 * An hourly series as the summary endpoint sends it: `start` is the epoch second of the first
 * bucket, `step` its width (3600), `values` one figure per bucket, oldest first.
 *
 * Bucket instants are epoch, so the browser labels them in ITS OWN clock. On the local product
 * that is the site's clock by definition; in the cloud an operator in another timezone reads a
 * site's day shifted to theirs, which is why the section header states whose midnight the window
 * belongs to rather than leaving the axis to imply it.
 */
export interface Series {
  start: number;
  step: number;
  values: number[];
}

/** A series with nothing in it is not a chart — callers use this to fall back to plain figures. */
export function hasSeries(s?: Series | null): s is Series {
  return !!s && Array.isArray(s.values) && s.values.length > 0;
}

/**
 * The tallest value in the series — the number every mark's height is drawn against.
 *
 * Callers state this beside the chart. Without it a bar row is a shape with no scale: the reader
 * can see that three o'clock was busier than four and has no way to tell whether the tallest bar
 * is four people or forty.
 */
export function seriesMax(s: Series): number {
  return Math.max(0, ...s.values);
}

/**
 * The clock reading for bucket `i`, e.g. `08:00`.
 *
 * 24-hour regardless of locale: an axis end has room for four characters, and `08:00 AM` either
 * wraps or gets truncated to something that reads as a different time.
 */
export function bucketLabel(s: Series, i: number): string {
  return new Date((s.start + i * s.step) * 1000).toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

/** Soft version of a mark's colour, for the buckets that aren't the point. */
const tint = (c: string, pct: number) => `color-mix(in srgb, ${c} ${pct}%, transparent)`;

/**
 * The track every empty slot and every meter sits on. Mixed off the ink colour rather than
 * `--muted`, which is almost indistinguishable from the recessed tile surface in light mode.
 */
const TRACK = 'color-mix(in srgb, var(--muted-foreground) 20%, transparent)';

/* ─── Axis row ─── */

/**
 * The line under a chart that says what its ends are. Three slots because that is what a card
 * has room for: where the series starts, the one thing worth calling out in the middle, and
 * where it ends.
 */
export function AxisRow({ left, center, right }: { left?: ReactNode; center?: ReactNode; right?: ReactNode }) {
  return (
    <div className="mt-1 flex items-baseline justify-between gap-1 text-[10px] leading-none text-muted-foreground">
      <span className="flex-none tabular-nums">{left}</span>
      <span className="min-w-0 flex-1 truncate text-center">{center}</span>
      <span className="flex-none tabular-nums">{right}</span>
    </div>
  );
}

/* ─── Sparkline ─── */

/**
 * Change over time as a line with the area under it, for a series whose SHAPE is the message —
 * when the alerts came, not how many arrived in any one hour.
 *
 * `step` is not a style choice, it is what the series IS. A sloped segment between two readings
 * claims the value passed through every point in between; that is true of a level that was
 * sampled (occupancy went from 2 to 4, so it was 3 at some moment) and false of a count that was
 * bucketed (12 detections in one hour and 4 in the next never "passed through" 8). Levels get
 * `step`, which holds each reading until the next one and changes in one move, so the flat runs
 * read as "nothing changed" rather than as a gentle drift nobody measured.
 *
 * The plot is stretched to the card's width by `preserveAspectRatio="none"`, which would also
 * stretch the stroke; `vectorEffect="non-scaling-stroke"` is what keeps it a 2px line at every
 * card width. The endpoint marker is HTML rather than an SVG circle for the same reason — a
 * circle in a non-uniformly scaled viewBox draws as an ellipse.
 */
export function Sparkline({
  series,
  color,
  height,
  step: stepped = false,
  ariaLabel,
}: {
  series: Series;
  color: string;
  /**
   * Fixed height in px. Omit it and the chart FILLS its container instead — which is how a card
   * absorbs the space a grid row gives it: the plot grows and the extra room holds data rather
   * than nothing. See `Plot` in AiAnalyticsCards.
   */
  height?: number;
  /** Hold each reading until the next, for a LEVEL rather than a per-bucket count. */
  step?: boolean;
  ariaLabel?: string;
}) {
  const values = series.values;
  const max = Math.max(...values, 1);
  const W = 100;
  const H = 40;
  // A single bucket has no line to draw, so it is plotted as a flat one — the area still reads
  // as "this much, throughout", which is the truth about a one-hour-old window.
  const step = values.length > 1 ? W / (values.length - 1) : 0;
  const pt = (v: number, i: number) => [i * step, H - (v / max) * H] as const;
  const points = values.map(pt);
  const line = points
    .map(([x, y], i) => {
      if (i === 0) return `M${x.toFixed(2)},${y.toFixed(2)}`;
      // Across to this bucket's edge at the PREVIOUS level, then the change in one vertical move.
      const prevY = points[i - 1][1];
      return stepped
        ? `L${x.toFixed(2)},${prevY.toFixed(2)} L${x.toFixed(2)},${y.toFixed(2)}`
        : `L${x.toFixed(2)},${y.toFixed(2)}`;
    })
    .join(' ');
  const area = `${line} L${(values.length > 1 ? W : 0).toFixed(2)},${H} L0,${H} Z`;
  const last = points[points.length - 1];
  const lastPct = values.length > 1 ? 100 : 0;

  return (
    <div className={`relative w-full ${height == null ? 'h-full' : ''}`} style={height == null ? undefined : { height }}>
      <svg
        className="absolute inset-0 h-full w-full overflow-visible"
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="none"
        role="img"
        aria-label={ariaLabel}
      >
        <path d={area} fill={tint(color, 14)} stroke="none" />
        <path
          d={line}
          fill="none"
          stroke={color}
          strokeWidth={2}
          strokeLinejoin="round"
          strokeLinecap="round"
          vectorEffect="non-scaling-stroke"
        />
        {/* Hairline baseline — solid, one shade off the surface, never dashed. */}
        <line x1="0" y1={H} x2={W} y2={H} stroke={TRACK} strokeWidth={1} vectorEffect="non-scaling-stroke" />
      </svg>
      {/* Where the series ends, direct-labelled with a marker rather than a number — the
          headline figure above the chart already states the total. */}
      <span
        className="absolute h-2 w-2 -translate-x-1/2 -translate-y-1/2 rounded-full"
        style={{
          left: `${lastPct}%`,
          top: `${(last[1] / H) * 100}%`,
          backgroundColor: color,
          boxShadow: '0 0 0 2px var(--background)',
        }}
        title={`${values[values.length - 1]} at ${bucketLabel(series, values.length - 1)}`}
        aria-hidden
      />
    </div>
  );
}

/* ─── Hourly bars ─── */

/**
 * One bar per hour, for a series read bucket-by-bucket — "how busy was three o'clock" rather
 * than "which way is it trending".
 *
 * HTML, not SVG: these are rectangles anchored to a baseline in a flex row, and a div per bar
 * gets the 4px rounded data-end, the 2px gap and a real hover target for free.
 */
export function MiniBars({
  series,
  color,
  /** `peak` marks the tallest bar, `last` the current hour, `none` treats them all alike. */
  emphasis = 'peak',
  height,
  unit = '',
}: {
  series: Series;
  color: string;
  emphasis?: 'peak' | 'last' | 'none';
  /** Fixed height in px; omit to fill the container, so the bars take the card's spare room. */
  height?: number;
  unit?: string;
}) {
  const values = series.values;
  const max = Math.max(...values, 1);
  const peakIndex = values.indexOf(max);
  const markIndex = emphasis === 'peak' ? peakIndex : emphasis === 'last' ? values.length - 1 : -1;

  return (
    // The baseline is part of the mark, so the whole thing is one column box: the bar row takes
    // whatever height is going and the rule sits under it.
    <div className={`flex flex-col ${height == null ? 'h-full' : ''}`}>
      <div
        className="flex w-full flex-1 items-end gap-[2px]"
        style={height == null ? undefined : { height }}
        role="img"
      >
        {values.map((v, i) => {
          const marked = i === markIndex && v > 0;
          return (
            // The COLUMN is full height and carries the hover, not the bar: an empty hour has no
            // bar to hover, and a reader checking "was anything happening at 03:00" needs the
            // same target there as everywhere else.
            <div
              key={i}
              className="flex h-full min-w-0 flex-1 items-end"
              title={`${bucketLabel(series, i)} · ${v}${unit ? ` ${unit}` : ''}`}
            >
              {v > 0 && (
                <div
                  className="w-full rounded-t-[4px]"
                  style={{
                    height: `${Math.max(8, (v / max) * 100)}%`,
                    // 45%, not less: on the dark surface a lighter tint of the brand collapses
                    // into a muddy maroon that no longer reads as the emphasised bar's colour.
                    backgroundColor: marked ? color : tint(color, 45),
                  }}
                />
              )}
            </div>
          );
        })}
      </div>
      {/* The baseline is what makes an empty hour legible. A zero used to draw a 2px stub, which
          read as a small value in a colour nobody could name; against a hairline rule the same
          hour reads as what it is — a slot with nothing in it. */}
      <div className="h-px w-full" style={{ backgroundColor: TRACK }} />
    </div>
  );
}

/* ─── Ratio gauge ─── */

/**
 * ONE ratio against its whole, as an arc on a track — 5 of 24 present, 9 of 16 faces matched.
 *
 * Deliberately a gauge and not a two-slice pie. The question these cards ask is "how much of the
 * whole is this", which is a single quantity against a limit; splitting the ring into a coloured
 * "known" and a coloured "unknown" half would ask the reader to compare two arcs to answer a
 * question that has one number in it. The percentage sits in the hole and the parts are stated
 * as counts beside it, so the ring is the glance and the text is the instrument.
 */
/**
 * Rough advance width of one character, in em, for the bold numeric labels that sit inside a
 * ring. Digits are tabular here so they are all one width; `%` is the wide one and the whole
 * problem — it is what turns "21%" into "100%" and pushes the label into the arc.
 */
const emWidth = (text: string) =>
  [...text].reduce((w, c) => w + (c === '%' ? 0.86 : c === '.' || c === ' ' ? 0.3 : 0.6), 0);

/**
 * The font size that keeps a label INSIDE a ring's hole.
 *
 * The hole is `size - 2 * stroke` across, but a label is a rectangle in a circle, so the width
 * it can actually use is the inscribed square's — the diameter over √2, a third narrower than
 * the hole looks. At 74px with a 9px stroke that is 40px of usable width, and "100%" set at the
 * old fixed 15px wants ~40px of it: no margin at all, which is exactly what "100%" touching the
 * arc looks like. On the 58px ring the same label wanted 40px in a 28px box and simply ran over
 * the stroke on both sides.
 *
 * So the size follows the string rather than the other way round: the longest value a card can
 * show decides the type, and every shorter one is drawn at the cap. Floored, because a label
 * too small to read is not a fix — anything that hits the floor is a ring too small for the
 * value, and the caller should give it more room.
 */
export function ringFontSize(size: number, stroke: number, text: string, max = 15, min = 9): number {
  const usable = ((size - 2 * stroke) / Math.SQRT2) * 0.94;
  return Math.max(min, Math.min(max, Math.floor((usable / emWidth(text)) * 10) / 10));
}

export function Gauge({
  percent,
  caption,
  color,
  size = 74,
  legend,
  ariaLabel,
}: {
  percent: number;
  /** The word under the percentage — what the ratio IS. Kept to one short token. */
  caption: string;
  color: string;
  size?: number;
  /** Always rendered: the ring is a glance, these are the values. */
  legend: Array<{ key: string; label: string; value: ReactNode; color?: string }>;
  ariaLabel?: string;
}) {
  const STROKE = 9;
  const pct = Math.max(0, Math.min(100, Number.isFinite(percent) ? percent : 0));
  const radius = (size - STROKE) / 2;
  const circumference = 2 * Math.PI * radius;
  const arc = (pct / 100) * circumference;
  const label = String(Math.round(pct));

  return (
    <div className="flex items-center gap-3">
      <div className="relative flex-none" style={{ width: size, height: size }}>
        <svg
          width={size}
          height={size}
          viewBox={`0 0 ${size} ${size}`}
          role="img"
          aria-label={ariaLabel}
        >
          <title>{`${Math.round(pct)}% ${caption}`}</title>
          <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke={TRACK} strokeWidth={STROKE} />
          {arc > 0 && (
            <g transform={`rotate(-90 ${size / 2} ${size / 2})`}>
              <circle
                cx={size / 2}
                cy={size / 2}
                r={radius}
                fill="none"
                stroke={color}
                strokeWidth={STROKE}
                strokeDasharray={`${arc} ${circumference - arc}`}
                strokeLinecap="butt"
              />
            </g>
          )}
        </svg>
        {/* Ink tokens, never the arc colour — the mark carries the identity, the text carries
            the value.

            The caption is dropped on a small ring rather than shrunk or clipped: the hole is
            `size - 2 * stroke` wide, so at 58px there are 40px for a word like "MATCHED" that
            wants 44 — and a caption bleeding over its own arc is worse than no caption. The
            percentage always fits, the legend beside it names the parts, and the full phrase
            stays reachable as the ring's tooltip. */}
        <div className="absolute inset-0 flex flex-col items-center justify-center px-1">
          <span
            className="font-bold leading-none tabular-nums text-foreground"
            // Sized to the string, not fixed: see ringFontSize. 100% is three digits and a wide
            // glyph, and it is the one value every gauge eventually shows.
            style={{ fontSize: `${ringFontSize(size, STROKE, `${label}%`)}px` }}
          >
            {label}%
          </span>
          {size >= 68 && (
            <span className="mt-0.5 text-[8px] font-semibold uppercase tracking-wider text-muted-foreground">
              {caption}
            </span>
          )}
        </div>
      </div>

      <ul className="min-w-0 flex-1 space-y-1.5">
        {legend.map((l) => (
          <li key={l.key} className="flex items-center gap-1.5 text-[11px] leading-tight">
            {l.color && (
              <span className="h-1.5 w-1.5 flex-none rounded-full" style={{ backgroundColor: l.color }} aria-hidden />
            )}
            <span className="min-w-0 flex-1 truncate text-muted-foreground">{l.label}</span>
            <span className="flex-none font-semibold tabular-nums text-foreground">{l.value}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ─── Stacked segment bar ─── */

/**
 * A whole split into a handful of named parts — open alerts by severity.
 *
 * Segments are separated by a 2px gap in the surface colour rather than by a border: a stroke
 * around a mark competes with the mark. Parts with a zero count are dropped from the bar and
 * kept in the legend, because "0 critical" is worth reading and a zero-width segment is not.
 */
export function SegmentedBar({
  segments,
  height = 6,
}: {
  segments: Array<{ key: string; label: string; value: number; color: string }>;
  height?: number;
}) {
  const total = segments.reduce((acc, s) => acc + Math.max(0, s.value), 0);
  const present = segments.filter((s) => s.value > 0);

  return (
    <div>
      <div
        className="flex w-full overflow-hidden rounded-full"
        style={{ height, backgroundColor: total > 0 ? 'transparent' : TRACK, gap: present.length > 1 ? 2 : 0 }}
        role="img"
        aria-label={segments.map((s) => `${s.label} ${s.value}`).join(', ')}
      >
        {present.map((s) => (
          <div
            key={s.key}
            className="h-full rounded-full"
            // A segment under ~2% of the bar would round to nothing; the floor keeps the one
            // critical alert out of two hundred visible, which is the whole point of the bar.
            style={{ flexGrow: Math.max(s.value / total, 0.02), backgroundColor: s.color }}
            title={`${s.label}: ${s.value}`}
          />
        ))}
      </div>
      {/* The relief that makes the bar safe — see rule 2 in the file header. */}
      <ul className="mt-1.5 flex flex-wrap items-center gap-x-2.5 gap-y-1">
        {segments.map((s) => (
          <li key={s.key} className="flex items-center gap-1 text-[10px] leading-none">
            <span className="h-1.5 w-1.5 flex-none rounded-full" style={{ backgroundColor: s.color }} aria-hidden />
            <span className="text-muted-foreground">{s.label}</span>
            <span className="font-semibold tabular-nums text-foreground">{s.value}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
